/**
 * @fileoverview Build bundled artwork SVG strings without external references.
 * @module ui-artwork/buildArtworkSvg
 *
 * Responsibilities:
 * - Render registered glyphs and authored family bodies as inline SVG.
 * - Apply the catalog color policy and optional presentation layers.
 *
 * Dependencies: lucide-react, react-dom/server, catalog, color policy, SVG registry.
 * Architecture: Pure SVG factory; no network or filesystem access at runtime.
 * Notes: Missing authored bodies fail closed rather than selecting generic art.
 */
import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { LUCIDE_COMPONENTS, resolveLucideName } from './catalog/glyphs';
import { getArtworkEntry } from './catalog';
import { applyColorPolicy } from './colorPolicy';
import { getAuthoredSvgBody } from './svg';

export interface BuildArtworkSvgOptions {
  color?: string;
  size?: number;
  plinth?: boolean;
  halo?: boolean;
}

const xmlColor = (value: string) => /^#[\da-f]{3}(?:[\da-f]{3})?$/i.test(value) ? value : '#475569';

/** @reusable buildArtworkSvg */
export function buildArtworkSvg(id: string, options: BuildArtworkSvgOptions = {}): string {
  const entry = getArtworkEntry(id);
  const size = Math.max(1, Math.floor(options.size ?? 64));
  if (!entry) {
    const color = xmlColor(options.color ?? '#475569');
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 96 96" fill="none"><circle cx="48" cy="48" r="34" stroke="${color}" stroke-width="6"/><path d="M38 38a10 10 0 1 1 17 7c-5 4-7 6-7 12" stroke="${color}" stroke-width="6" stroke-linecap="round"/><circle cx="48" cy="68" r="3" fill="${color}"/></svg>`;
  }
  const color = xmlColor(applyColorPolicy(entry, options.color).color);
  const glyphName = resolveLucideName(entry.id);
  const glyph = glyphName && LUCIDE_COMPONENTS[glyphName];
  const authoredBody = glyph ? undefined : getAuthoredSvgBody(entry.id);
  if (!glyph && !authoredBody) throw new Error(`Missing authored SVG body for ${entry.id}`);
  const body = glyph
    ? renderToStaticMarkup(React.createElement(glyph, { color, size: 70, strokeWidth: 1.8, 'aria-hidden': true }))
      .replace(' xmlns="http://www.w3.org/2000/svg"', '')
    : authoredBody!(color);
  const plinth = options.plinth ? `<ellipse cx="48" cy="86" rx="34" ry="7" fill="${color}" opacity=".2"/><path d="M18 77h60l-7 9H25z" fill="${color}" opacity=".76"/>` : '';
  const halo = options.halo ? `<circle cx="48" cy="48" r="44" fill="none" stroke="#7c3aed" stroke-width="3"/>` : '';
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 96 96" fill="none">${halo}${plinth}${body}</svg>`;
}
